import { createTheme, responsiveFontSizes } from "@mui/material/styles";

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
};

const responsiveTypography = (defaultSize, mobileSize) => ({
  fontSize: defaultSize,
  [`@media (max-width:${breakpoints.values.sm}px)`]: {
    fontSize: mobileSize,
  },
});

const sharedComponents = (primaryMain) => ({
  MuiTextField: {
    styleOverrides: {
      root: {
        "&.MuiTextField-sizeSmall": {
          height: "2rem",
          "& .MuiInputBase-root": { height: "2rem", fontSize: "0.875rem" },
          "& input": { padding: "0.25rem 0.5rem" },
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: { borderRadius: "8px", fontWeight: 500 },
      containedPrimary: {
        boxShadow: `0 2px 4px ${primaryMain}33`,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        color: "#333",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      },
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: { borderRadius: "8px", padding: "10px 20px" },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: "16px",
        boxShadow: `0 4px 12px ${primaryMain}1a`,
        border: "1px solid #f5f5f5",
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { boxShadow: `0 4px 12px ${primaryMain}1a` },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      root: { width: 240, height: "100%" },
    },
  },
  MuiSvgIcon: {
    styleOverrides: {
      root: {
        color: primaryMain,
        "&.MuiSvgIcon-colorError": { color: "#ef4444" },
        "&.MuiSvgIcon-colorSuccess": { color: "#4caf50" },
        "&.MuiSvgIcon-colorWarning": { color: "#ff9800" },
        "&.MuiSvgIcon-colorInfo": { color: "#3b82f6" },
        ".MuiButton-containedPrimary &, .MuiButton-containedSecondary &": {
          color: "#ffffff",
        },
        ".MuiSnackbar-root &, .MuiAlert-root &, .MuiChip-filled &": {
          color: "#ffffff",
        },
        ".MuiAppBar-root &": { color: "#333" },
        ".dark-background &": { color: "#ffffff" },
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        color: primaryMain,
        textDecorationColor: `${primaryMain}66`,
        "&:hover": { textDecorationColor: `${primaryMain}cc` },
      },
    },
  },
});

const buildTheme = ({ primary, secondary, background, headingColor }) =>
  responsiveFontSizes(
    createTheme({
      breakpoints,
      typography: {
        fontFamily: "Roboto, sans-serif",
        fontSize: 14,
        h1: { fontWeight: 500, color: headingColor, ...responsiveTypography("1.7rem", "1.4rem") },
        h2: { fontWeight: 500, color: headingColor, ...responsiveTypography("1.3rem", "1.1rem") },
        h3: { fontWeight: 500, color: headingColor, ...responsiveTypography("1.5rem", "1.25rem") },
        h4: { fontWeight: 500, color: headingColor, ...responsiveTypography("1.4rem", "1.15rem") },
        h5: { fontWeight: 600, color: headingColor, ...responsiveTypography("1.2rem", "1rem") },
        h6: { fontWeight: 600, color: headingColor, ...responsiveTypography("1rem", "0.875rem") },
        body1: { color: "#555", ...responsiveTypography("1rem", "0.875rem") },
        body2: { color: "#666", ...responsiveTypography("0.875rem", "0.75rem") },
        button: { textTransform: "none", fontWeight: 500, ...responsiveTypography("1rem", "0.875rem") },
      },
      palette: {
        primary,
        secondary,
        info: { main: "#3b82f6" },
        success: { main: "#4caf50" },
        warning: { main: "#ff9800" },
        error: { main: "#ef4444" },
        background: { default: background },
      },
      components: sharedComponents(primary.main),
    })
  );

// Admin — deep purple, authoritative
export const adminTheme = buildTheme({
  primary:     { main: "#7c3aed", light: "#a78bfa", dark: "#5b21b6", contrastText: "#ffffff" },
  secondary:   { main: "#c026d3", light: "#e879f9", dark: "#86198f", contrastText: "#ffffff" },
  background:  "#faf5ff",
  headingColor: "#7c3aed",
});

// Manager — teal/emerald, operational
export const managerTheme = buildTheme({
  primary:     { main: "#0d9488", light: "#5eead4", dark: "#0f766e", contrastText: "#ffffff" },
  secondary:   { main: "#0891b2", light: "#67e8f9", dark: "#0e7490", contrastText: "#ffffff" },
  background:  "#f0fdfa",
  headingColor: "#0d9488",
});

// Instructor — blue, professional/educational
export const instructorTheme = buildTheme({
  primary:     { main: "#2563eb", light: "#60a5fa", dark: "#1e40af", contrastText: "#ffffff" },
  secondary:   { main: "#7c3aed", light: "#a78bfa", dark: "#5b21b6", contrastText: "#ffffff" },
  background:  "#eff6ff",
  headingColor: "#2563eb",
});

// Student — orange, energetic/friendly (original colors)
export const studentTheme = buildTheme({
  primary:     { main: "#f97316", light: "#fdba74", dark: "#ea580c", contrastText: "#ffffff" },
  secondary:   { main: "#3b82f6", light: "#60a5fa", dark: "#1e40af", contrastText: "#ffffff" },
  background:  "#fdf9f5",
  headingColor: "#f97316",
});

// Default (unauthenticated / landing page) — same as student
export default studentTheme;
